import { useEffect, useState } from 'react';

/**
 * SSR-safe useMediaQuery. Always calls hooks in the same order (unlike
 * beautiful-react-hooks' useMediaQuery which conditionally skips useState
 * on the server, breaking React hydration).
 *
 * Uses a lazy initializer to read the real matchMedia value on the client's
 * first render, avoiding the false→true flip that causes CLS.
 */
export default function useMediaQuerySafe(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);

  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);

    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
